import { useCallback, useEffect, useMemo, useState } from 'react';
import { camelizeKeys } from 'humps';
import usePassengerCitySelector from './usePassengerCitySelector';
import usePurchase from './store/usePurchase';
import { getMasterCities } from '../services/masterCities';
import errorMonitoring from '../errorMonitoring';

/**
 * Maps the master city catalog to `FilterableSelect` options. The value is the
 * provider city id (kept as a string) echoed back as the passenger's meta
 * `cityId`; the label mirrors the legacy "CITY - DEPARTMENT" format.
 * @param {Array} cities
 * @returns {Array<{value: string, label: string}>}
 */
const toOptions = (cities) =>
  (cities || [])
    .filter((city) => city && city.id != null)
    .map((city) => ({
      value: String(city.id),
      label: city.department ? `${city.city} - ${city.department}` : city.city,
    }));

/**
 * Fetches the transporter's master city catalog once at checkout, gated per
 * tenant by the `ENABLE_PASSENGER_CITY_SELECTOR` feature flag (see
 * `usePassengerCitySelector`). Returns memoized select options so a large
 * catalog is transformed a single time.
 *
 * The catalog itself is resolved per transporter, not per brand: the request
 * goes to the purchase API (`GET /api/v2/master_cities`), which proxies
 * saas-integrations `GET /master-cities/{ws_path_abbr}`. Any provider that
 * registers that route can enable the selector by configuration alone.
 *
 * @returns {{ options: Array, loading: boolean, enabled: boolean, error: boolean, retry: Function }}
 * `enabled` is the feature flag; `error` is true when the catalog fetch failed
 * (the required select must then show a retry affordance instead of an empty
 * list); `retry` re-runs the fetch.
 */
function useMasterCities() {
  const enabled = usePassengerCitySelector();
  const purchase = usePurchase();
  const token = purchase?.token;

  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  const retry = useCallback(() => setReloadNonce((nonce) => nonce + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;

    // No token means no catalog can be resolved, so drop whatever is held instead of just
    // bailing out: these are provider city ids that feed the DIAN invoice, and leaving a
    // previous purchase's ids selectable is how a legally wrong city gets submitted.
    if (!token) {
      setCities([]);
      setError(false);
      setLoading(false);
      return undefined;
    }

    let active = true;
    // Clear any catalog left from a previous token before fetching, so a stale
    // provider city id can never stay selectable while the new request is in
    // flight. Results are applied only while `active`, i.e. only for the token
    // this effect ran with.
    setCities([]);
    setError(false);
    setLoading(true);

    getMasterCities(token)
      .then((response) => {
        if (active) setCities(camelizeKeys(response) || []);
      })
      .catch((fetchError) => {
        errorMonitoring.notify({ error: fetchError, info: { from: 'useMasterCities' } });
        if (active) {
          setCities([]);
          setError(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [enabled, token, reloadNonce]);

  const options = useMemo(() => toOptions(cities), [cities]);

  return { options, loading, enabled, error, retry };
}

export default useMasterCities;
